import { createContext, type PropsWithChildren } from 'react'

import { useBlogList } from '@/blocks/BlogList/useBlogList'
import type { ProductKeys } from '@/components/Products/constants'
import { DEFAULT_ARTICLES_PER_PAGE, PICKED_FIELDS } from '@/core/blog/constants'
import type { CardArticleEntryData } from '@/models'
import { getArticlesQuery } from '@/server/services/builder/article'

const ARTICLES_DEFAULT_VALUE = {}

type ArticlesContextInputProps = PropsWithChildren<{
  articles: CardArticleEntryData[]
  perPage?: number
  product?: ProductKeys
  sort: object
  query?: object
}>

type ArticlesContextOutputProps = PropsWithChildren<{
  articlesListState: {
    page: number
    hasMore: boolean
    loading: boolean
    list: CardArticleEntryData[]
    firstNewPostIndex: number
  }
  handleShowMore: () => Promise<void>
  perPage: number
}>

export const ArticlesContext = createContext<ArticlesContextOutputProps>(
  ARTICLES_DEFAULT_VALUE as ArticlesContextOutputProps,
)

export const ArticlesContextProvider = ({
  product,
  articles,
  sort,
  query,
  perPage = DEFAULT_ARTICLES_PER_PAGE,
  children,
}: ArticlesContextInputProps) => {
  const { handleShowMore, state: articlesListState } = useBlogList({
    articles,
    perPage,
    query: query || getArticlesQuery(product),
    fields: PICKED_FIELDS,
    sort,
  })

  return (
    <ArticlesContext.Provider value={{ handleShowMore, articlesListState, perPage }}>
      {children}
    </ArticlesContext.Provider>
  )
}
